import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('number-field')
export class NumberField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: Number }) value = 0;
  @property({ type: String }) placeholder = '';
  @property({ type: Boolean }) required = false;
  @property({ type: Number }) min?: number;
  @property({ type: Number }) max?: number;
  @property({ type: Number }) step?: number;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;
  @property({ type: String }) unit = ''; // e.g., '$', '%', 'kg'

  @state() private _isHolding = false;
  @state() private _holdInterval?: number;

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 16px;
      position: relative;
    }
    .field-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 10px;
    }
    .field-label {
      font-weight: 600;
      color: #1f2937;
      font-size: 14px;
      letter-spacing: -0.01em;
      transition: color 0.2s;
    }
    .field-label:focus-within {
      color: #6366f1;
    }
    .required {
      color: #ef4444;
      margin-left: 2px;
    }
    .field-hint {
      font-size: 12px;
      color: #6b7280;
      font-weight: 400;
    }

    /* Input Container */
    .input-wrapper {
      position: relative;
      display: inline-flex;
      align-items: stretch;
      background: #ffffff;
      border: 2px solid #e5e7eb;
      border-radius: 12px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
      min-height: 48px;
      max-width: 280px;
    }

    .input-wrapper:hover {
      border-color: #cbd5e1;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    }

    .input-wrapper:focus-within {
      border-color: #6366f1;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
    }

    .input-wrapper.error {
      border-color: #ef4444;
      animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97);
    }

    .input-wrapper.error:focus-within {
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
    }

    /* Input Field with Unit Support */
    .input-container {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      position: relative;
      padding: 0 16px;
    }

    input {
      width: 100%;
      padding: 12px 0;
      border: none;
      background: transparent;
      font-size: 16px;
      font-weight: 600;
      color: #111827;
      text-align: left;
      -moz-appearance: textfield;
    }

    /* Hide spinner buttons */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input:focus {
      outline: none;
    }

    input::placeholder {
      color: #9ca3af;
      font-weight: 400;
    }

    .unit {
      margin-left: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #6b7280;
      flex-shrink: 0;
    }

    /* Value Display Enhancement - Tabular Numerals */
    input[type='number'] {
      font-feature-settings: 'tnum';
      font-variant-numeric: tabular-nums;
    }

    /* Range Indicator */
    .range-info {
      font-size: 11px;
      color: #9ca3af;
      margin-top: 6px;
      text-align: center;
      font-weight: 500;
    }

    .error-message {
      color: #dc2626;
      font-size: 12px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      animation: slideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .error-message::before {
      content: '⚠';
      font-size: 14px;
    }

    @keyframes shake {
      0%,
      100% {
        transform: translateX(0);
      }
      10%,
      30%,
      50%,
      70%,
      90% {
        transform: translateX(-4px);
      }
      20%,
      40%,
      60%,
      80% {
        transform: translateX(4px);
      }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Subtle pulse on value change */
    @keyframes value-pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.08);
      }
      100% {
        transform: scale(1);
      }
    }

    .value-changed {
      animation: value-pulse 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  private _handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.valueAsNumber || 0;
    this._dispatchChange();
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Keyboard support for arrow keys (NN/g recommendation)
  private _handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._handleIncrement();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._handleDecrement();
    }
  }

  private _handleDecrement() {
    const currentValue = this.value ?? 0;
    const step = this.step ?? 1;
    const newValue = Number((currentValue - step).toFixed(10)); // Prevent floating point errors

    if (this.min !== undefined && newValue < this.min) {
      return;
    }

    this.value = newValue;
    this._dispatchChange();
    this._triggerPulse();
  }

  private _handleIncrement() {
    const currentValue = this.value ?? 0;
    const step = this.step ?? 1;
    const newValue = Number((currentValue + step).toFixed(10)); // Prevent floating point errors

    if (this.max !== undefined && newValue > this.max) {
      return;
    }

    this.value = newValue;
    this._dispatchChange();
    this._triggerPulse();
  }

  private _dispatchChange() {
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Long-press support for continuous increment/decrement (NN/g recommendation)
  private _startHold(callback: () => void) {
    this._isHolding = true;
    callback(); // Immediate first action

    // Initial delay before continuous action
    setTimeout(() => {
      if (this._isHolding) {
        this._holdInterval = window.setInterval(callback, 100);
      }
    }, 500);
  }

  private _stopHold() {
    this._isHolding = false;
    if (this._holdInterval) {
      clearInterval(this._holdInterval);
      this._holdInterval = undefined;
    }
  }

  private _triggerPulse() {
    const input = this.shadowRoot?.querySelector('input');
    if (input) {
      input.classList.add('value-changed');
      setTimeout(() => input.classList.remove('value-changed'), 300);
    }
  }

  private _isDecrementDisabled(): boolean {
    if (this.min === undefined) return false;
    const currentValue = this.value ?? 0;
    return currentValue <= this.min;
  }

  private _isIncrementDisabled(): boolean {
    if (this.max === undefined) return false;
    const currentValue = this.value ?? 0;
    return currentValue >= this.max;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopHold();
  }

  render() {
    const hasError = this.error && this.touched;
    const showRange = this.min !== undefined || this.max !== undefined;

    return html`
      <div class="field-group">
        ${this.label
          ? html`
              <div class="field-header">
                <label class="field-label">
                  ${this.label}
                  ${this.required ? html`<span class="required">*</span>` : ''}
                </label>
                ${showRange
                  ? html`
                      <span class="field-hint">
                        ${this.min !== undefined ? `Min: ${this.min}` : ''}
                        ${this.min !== undefined && this.max !== undefined
                          ? ' • '
                          : ''}
                        ${this.max !== undefined ? `Max: ${this.max}` : ''}
                      </span>
                    `
                  : ''}
              </div>
            `
          : ''}
        <div class="input-wrapper ${hasError ? 'error' : ''}">
          <div class="input-container">
            <input
              type="number"
              .value=${this.value?.toString() || ''}
              placeholder=${this.placeholder || '0'}
              ?required=${this.required}
              min=${this.min !== undefined ? this.min : ''}
              max=${this.max !== undefined ? this.max : ''}
              step=${this.step !== undefined ? this.step : ''}
              @input=${this._handleInput}
              @blur=${this._handleBlur}
              aria-valuemin=${this.min !== undefined ? this.min : ''}
              aria-valuemax=${this.max !== undefined ? this.max : ''}
              aria-valuenow=${this.value !== undefined ? this.value : ''}
              aria-label=${this.label || 'Number input'} />
            ${this.unit ? html`<span class="unit">${this.unit}</span>` : ''}
          </div>
        </div>
        ${hasError
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }
}
